// Chemin: C:\gestion-copro\src\pages\public\CGUPage.tsx
// Page Conditions Générales d'Utilisation
// Session 78 : Création initiale
// Session 186 : Refonte complète — VIGIE + CS, RGPD complet, tarifs à jour, rétractation, portabilité

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText,
  Users,
  CreditCard,
  Shield,
  AlertTriangle,
  Scale,
  Ban,
  RefreshCw,
  Brain,
  Download,
  Clock,
  Edit
} from 'lucide-react';

const CGUPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header simple */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
              Retour à l'accueil
            </Link>
            <img 
              src="/logo-copro-protect.png" 
              alt="Copro Protect" 
              className="h-10 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Contenu */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Conditions Générales d'Utilisation</h1>
        <p className="text-gray-500 mb-8">Dernière mise à jour : 1er mars 2026</p>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-200">
          
          {/* Article 1 : Objet */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Article 1 — Objet</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>
                Les présentes Conditions Générales d'Utilisation (ci-après "CGU") ont pour objet de définir 
                les modalités et conditions d'utilisation de la plateforme <strong>Copro Protect</strong>, 
                éditée par la société DOM TECH & SERVICES (SASU), immatriculée au RCS de Boulogne-sur-Mer 
                sous le numéro 939 094 231.
              </p>
              <p>
                Copro Protect est une solution SaaS (Software as a Service) destinée à deux types d'utilisateurs :
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>VIGIE</strong> : copropriétaires individuels souhaitant protéger leurs intérêts 
                  dans leur copropriété (gestion documentaire, correspondances, signalements, assistant IA)
                </li>
                <li>
                  <strong>CS (Conseil Syndical)</strong> : conseils syndicaux de copropriétés souhaitant 
                  structurer et sécuriser leur gestion collective
                </li>
              </ul>
              <p>
                La plateforme intègre un assistant d'intelligence artificielle dénommé SENTINELLE, dont les 
                conditions d'utilisation sont détaillées à l'article 4.
              </p>
              <p>
                L'utilisation de la plateforme implique l'acceptation pleine et entière des présentes CGU.
              </p>
            </div>
          </section>

          {/* Article 2 : Accès et inscription */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Article 2 — Accès et inscription</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p><strong>2.1 Offre VIGIE</strong></p>
              <p>
                Tout copropriétaire majeur peut s'inscrire de manière autonome à l'offre VIGIE depuis le 
                site internet de Copro Protect. L'inscription requiert une adresse email valide et la 
                souscription d'un abonnement payant.
              </p>

              <p><strong>2.2 Offre CS</strong></p>
              <p>
                L'accès à l'offre CS est réservé aux conseils syndicaux de copropriétés. L'inscription est 
                effectuée par un membre habilité du conseil syndical. Chaque utilisateur reçoit des 
                identifiants personnels et confidentiels.
              </p>
              
              <p><strong>2.3 Engagements de l'utilisateur</strong></p>
              <p>L'utilisateur s'engage à :</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Fournir des informations exactes lors de son inscription</li>
                <li>Maintenir la confidentialité de ses identifiants</li>
                <li>Notifier immédiatement tout usage non autorisé de son compte</li>
                <li>Ne pas partager son compte avec des tiers</li>
              </ul>

              <p><strong>2.4 Âge minimum</strong></p>
              <p>
                L'utilisation de Copro Protect est réservée aux personnes majeures (18 ans et plus).
              </p>
            </div>
          </section>

          {/* Article 3 : Abonnement et tarification */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Article 3 — Abonnement et tarification</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p><strong>3.1 Offre VIGIE</strong></p>
              <div className="bg-slate-50 rounded-lg p-4 my-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-semibold">Formule</th>
                      <th className="text-left py-2 font-semibold">Mensuel TTC</th>
                      <th className="text-left py-2 font-semibold">Annuel TTC</th>
                      <th className="text-left py-2 font-semibold">Analyses IA</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Fondateur</td>
                      <td className="py-2">5,90 €/mois</td>
                      <td className="py-2">59 €/an</td>
                      <td className="py-2">100/an</td>
                    </tr>
                    <tr>
                      <td className="py-2">Normal</td>
                      <td className="py-2">14,90 €/mois</td>
                      <td className="py-2">149 €/an</td>
                      <td className="py-2">100/an</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-500">
                La formule Fondateur est réservée aux premiers inscrits (20 places maximum). Le tarif 
                Fondateur est verrouillé à vie pour les bénéficiaires tant que l'abonnement reste actif. 
                En cas de résiliation puis réinscription, le tarif Normal s'appliquera.
              </p>

              <p className="mt-4"><strong>3.2 Offre CS (Conseil Syndical)</strong></p>
              <div className="bg-slate-50 rounded-lg p-4 my-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-semibold">Formule</th>
                      <th className="text-left py-2 font-semibold">Cible</th>
                      <th className="text-left py-2 font-semibold">Mensuel TTC</th>
                      <th className="text-left py-2 font-semibold">Annuel TTC</th>
                      <th className="text-left py-2 font-semibold">Analyses IA</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">SENTINELLE</td>
                      <td className="py-2">≤ 15 lots</td>
                      <td className="py-2">23,90 €/mois</td>
                      <td className="py-2">239 €/an</td>
                      <td className="py-2">200/an</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">BOUCLIER</td>
                      <td className="py-2">16-40 lots</td>
                      <td className="py-2">41,90 €/mois</td>
                      <td className="py-2">419 €/an</td>
                      <td className="py-2">300/an</td>
                    </tr>
                    <tr>
                      <td className="py-2">FORTERESSE</td>
                      <td className="py-2">&gt; 40 lots</td>
                      <td className="py-2">59,90 €/mois</td>
                      <td className="py-2">599 €/an</td>
                      <td className="py-2">600/an</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p><strong>3.3 Pack de recharge</strong></p>
              <p>
                En cas d'épuisement du quota d'analyses SENTINELLE, un pack de 100 analyses supplémentaires 
                peut être acquis au tarif de 29 € TTC. Les analyses non consommées en fin de période 
                d'abonnement ne sont pas reportées sur la période suivante.
              </p>
              
              <p><strong>3.4 Paiement</strong></p>
              <p>
                Le paiement est effectué par carte bancaire via la plateforme sécurisée Stripe. L'abonnement 
                mensuel est prélevé chaque mois à date anniversaire. L'abonnement annuel est prélevé en une 
                fois à la date de souscription puis à chaque date anniversaire.
              </p>

              <p><strong>3.5 Clause de révision tarifaire</strong></p>
              <p>
                Les tarifs peuvent être révisés annuellement, notamment en cas d'évolution significative 
                des coûts d'infrastructure ou des API d'intelligence artificielle. Toute modification 
                tarifaire sera notifiée aux utilisateurs avec un préavis minimum de 30 jours avant son 
                entrée en vigueur. Les abonnés Fondateur conservent leur tarif verrouillé 
                indépendamment des révisions tarifaires.
              </p>
            </div>
          </section>

          {/* Article 4 : SENTINELLE - Intelligence Artificielle */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Article 4 — SENTINELLE (Intelligence Artificielle)</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p><strong>4.1 Nature du service</strong></p>
              <p>
                SENTINELLE est un assistant basé sur l'intelligence artificielle intégré à Copro Protect. 
                Il propose des analyses, suggestions et contenus générés automatiquement. SENTINELLE 
                fonctionne selon le paradigme "SENTINELLE propose, l'humain valide".
              </p>
              
              <p><strong>4.2 Limites de l'IA</strong></p>
              <p>
                Les contenus générés par SENTINELLE sont fournis à titre informatif et ne constituent 
                en aucun cas un conseil juridique, comptable ou professionnel. L'utilisateur reste seul 
                responsable de la validation et de l'utilisation des contenus proposés. SENTINELLE ne se 
                substitue pas à un avocat, un notaire, un expert-comptable ou tout autre professionnel 
                réglementé.
              </p>
              
              <p><strong>4.3 Quota d'analyses</strong></p>
              <p>Chaque appel à SENTINELLE consomme une analyse du quota inclus dans l'abonnement :</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Classement automatique d'un document</li>
                <li>Génération d'un courrier</li>
                <li>Analyse d'une annexe comptable</li>
                <li>Détection d'anomalies dans un email</li>
                <li>Question posée à l'assistant conversationnel</li>
              </ul>
              <p className="mt-2">
                Une alerte est envoyée lorsque 80% du quota est atteint. À 100%, les fonctionnalités 
                SENTINELLE sont suspendues jusqu'à l'achat d'un pack de recharge ou le renouvellement 
                de l'abonnement.
              </p>

              <p><strong>4.4 Traitement des données par l'IA</strong></p>
              <p>
                Les contenus soumis à SENTINELLE (documents, questions, courriers) sont transmis à 
                l'API Claude d'Anthropic pour traitement. Anthropic s'engage contractuellement à ne pas 
                utiliser ces données pour entraîner ses modèles. Les données sont traitées en temps réel 
                et ne sont pas stockées par Anthropic au-delà du temps nécessaire au traitement de la requête.
              </p>
            </div>
          </section>

          {/* Article 5 : Usages interdits */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Ban className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Article 5 — Usages interdits</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>Il est strictement interdit d'utiliser Copro Protect pour :</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Revente de service</strong> : proposer les fonctionnalités SENTINELLE à des 
                  tiers non concernés par la copropriété de l'abonné
                </li>
                <li>
                  <strong>Usage détourné</strong> : utiliser l'IA pour des besoins personnels ou 
                  professionnels sans rapport avec la gestion de copropriété
                </li>
                <li>
                  <strong>Mutualisation</strong> : partager un abonnement entre plusieurs copropriétés 
                  distinctes (un abonnement = une copropriété)
                </li>
                <li>
                  <strong>Extraction massive</strong> : collecter automatiquement les données ou 
                  contenus générés à des fins de constitution de base de données
                </li>
                <li>
                  <strong>Contenu illicite</strong> : stocker ou diffuser des contenus contraires 
                  aux lois en vigueur
                </li>
              </ul>
              <p className="mt-3">
                Tout manquement à ces règles pourra entraîner la suspension immédiate du compte, 
                sans remboursement et sans préjudice des poursuites éventuelles.
              </p>
            </div>
          </section>

          {/* Article 6 : Responsabilités */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Article 6 — Responsabilités</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p><strong>6.1 Responsabilité de l'éditeur</strong></p>
              <p>
                DOM TECH & SERVICES s'engage à assurer la disponibilité de la plateforme et la 
                sécurité des données. Toutefois, l'éditeur ne saurait être tenu responsable :
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Des interruptions temporaires pour maintenance ou mise à jour</li>
                <li>Des dysfonctionnements liés à des causes externes (hébergeur, réseau, navigateur)</li>
                <li>De l'utilisation faite par les utilisateurs des contenus générés par SENTINELLE</li>
                <li>Des conséquences des décisions prises sur la base des informations fournies</li>
                <li>De l'indisponibilité temporaire des services tiers (Stripe, Anthropic, Google)</li>
                <li>De la perte totale ou partielle de données résultant d'un événement de force majeure</li>
              </ul>
              
              <p className="mt-3"><strong>6.2 Responsabilité de l'utilisateur</strong></p>
              <p>
                L'utilisateur est seul responsable de l'utilisation qu'il fait de la plateforme, 
                des données qu'il y dépose, et des actions qu'il entreprend sur la base des 
                informations obtenues. Il lui appartient de vérifier et valider tout contenu généré 
                par SENTINELLE avant de l'utiliser.
              </p>

              <p className="mt-3"><strong>6.3 Sauvegarde des données et responsabilité partagée</strong></p>
              <p>
                DOM TECH & SERVICES met en œuvre des sauvegardes régulières des données hébergées 
                sur la plateforme. Toutefois, en cas de perte de données liée à un événement 
                de force majeure (défaillance critique de l'hébergeur, cyberattaque, catastrophe 
                naturelle, ou tout autre événement imprévisible et irrésistible), l'éditeur ne 
                saurait garantir la récupération intégrale des données.
              </p>
              <p>
                L'utilisateur est vivement encouragé à exporter régulièrement ses données via la 
                fonctionnalité « Exporter mes données » accessible depuis le menu de la plateforme. 
                Cet export constitue la meilleure protection contre toute perte de données, quelle 
                qu'en soit la cause.
              </p>
              <p>
                L'éditeur ne pourra être tenu responsable de la perte de données que l'utilisateur 
                n'aurait pas sauvegardées par le biais de cette fonctionnalité d'export. 
                Les données exportées hors de la plateforme sont sous la seule responsabilité 
                de l'utilisateur.
              </p>
            </div>
          </section>

          {/* Article 7 : Protection des données */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-teal-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Article 7 — Protection des données personnelles</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p><strong>7.1 Responsable de traitement</strong></p>
              <p>
                Le responsable du traitement des données personnelles est DOM TECH & SERVICES (SASU), 
                dont le siège social est situé à Boulogne-sur-Mer (62200). 
                Contact : <a href="mailto:contact@copro-protect.fr" className="text-blue-600 hover:underline">contact@copro-protect.fr</a>.
              </p>

              <p><strong>7.2 Données collectées et finalités</strong></p>
              <div className="bg-slate-50 rounded-lg p-4 my-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-semibold">Donnée</th>
                      <th className="text-left py-2 font-semibold">Finalité</th>
                      <th className="text-left py-2 font-semibold">Conservation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Email, nom, prénom</td>
                      <td className="py-2">Authentification, identification</td>
                      <td className="py-2">Durée du compte + 30 jours</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Documents uploadés</td>
                      <td className="py-2">Gestion documentaire (GED)</td>
                      <td className="py-2">Durée du compte + 30 jours</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Courriers, signalements</td>
                      <td className="py-2">Suivi correspondances et incidents</td>
                      <td className="py-2">Durée du compte + 30 jours</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Conversations SENTINELLE</td>
                      <td className="py-2">Historique de l'assistance IA</td>
                      <td className="py-2">Durée du compte + 30 jours</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Données de paiement</td>
                      <td className="py-2">Gestion des abonnements</td>
                      <td className="py-2">Traitées par Stripe (non stockées par Copro Protect)</td>
                    </tr>
                    <tr>
                      <td className="py-2">Logs d'actions</td>
                      <td className="py-2">Audit, sécurité</td>
                      <td className="py-2">1 an</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p><strong>7.3 Base légale des traitements</strong></p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Exécution du contrat</strong> (article 6.1.b RGPD) : traitement des données nécessaires à la fourniture du service</li>
                <li><strong>Intérêt légitime</strong> (article 6.1.f RGPD) : sécurité de la plateforme, prévention des fraudes</li>
                <li><strong>Obligation légale</strong> (article 6.1.c RGPD) : conservation des données de facturation</li>
              </ul>

              <p><strong>7.4 Sous-traitants</strong></p>
              <div className="bg-slate-50 rounded-lg p-4 my-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-semibold">Sous-traitant</th>
                      <th className="text-left py-2 font-semibold">Service</th>
                      <th className="text-left py-2 font-semibold">Localisation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Google Cloud (Firebase)</td>
                      <td className="py-2">Hébergement, base de données, stockage fichiers</td>
                      <td className="py-2">Europe (Paris — europe-west9)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Anthropic</td>
                      <td className="py-2">Intelligence artificielle (SENTINELLE)</td>
                      <td className="py-2">États-Unis (clauses contractuelles types)</td>
                    </tr>
                    <tr>
                      <td className="py-2">Stripe</td>
                      <td className="py-2">Paiement sécurisé</td>
                      <td className="py-2">Union européenne</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p><strong>7.5 Sécurité des données</strong></p>
              <p>Les données sont protégées par :</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Chiffrement en transit (TLS 1.3)</li>
                <li>Chiffrement au repos (AES-256)</li>
                <li>Contrôle d'accès par rôle (isolation des données entre utilisateurs)</li>
                <li>Sauvegardes quotidiennes automatiques</li>
              </ul>

              <p><strong>7.6 Droits de l'utilisateur</strong></p>
              <p>
                Conformément au RGPD, l'utilisateur dispose des droits suivants, exerçables par email 
                à <a href="mailto:contact@copro-protect.fr" className="text-blue-600 hover:underline">contact@copro-protect.fr</a> :
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Droit d'accès</strong> (article 15) : obtenir une copie de ses données personnelles</li>
                <li><strong>Droit de rectification</strong> (article 16) : corriger des données inexactes</li>
                <li><strong>Droit à l'effacement</strong> (article 17) : demander la suppression de ses données</li>
                <li><strong>Droit à la portabilité</strong> (article 20) : exporter ses données dans un format structuré et lisible (ZIP contenant JSON, CSV et fichiers originaux), accessible depuis le menu "Exporter mes données" de la plateforme</li>
                <li><strong>Droit d'opposition</strong> (article 21) : s'opposer au traitement de ses données</li>
                <li><strong>Droit à la limitation</strong> (article 18) : demander la restriction du traitement</li>
              </ul>
              <p className="mt-2">
                DOM TECH & SERVICES s'engage à répondre à toute demande dans un délai maximum de 30 jours. 
                En cas de difficulté, l'utilisateur peut adresser une réclamation à la CNIL 
                (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.cnil.fr</a>).
              </p>

              <p>
                Pour plus d'informations, veuillez consulter notre{' '}
                <Link to="/politique-confidentialite" className="text-blue-600 hover:underline">
                  Politique de confidentialité
                </Link>.
              </p>
            </div>
          </section>

          {/* Article 8 : Droit de rétractation */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Article 8 — Droit de rétractation</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p><strong>8.1 Délai de rétractation</strong></p>
              <p>
                Conformément aux articles L.221-18 et suivants du Code de la consommation, l'utilisateur 
                consommateur dispose d'un délai de 14 jours à compter de la souscription de son abonnement 
                pour exercer son droit de rétractation, sans avoir à justifier de motifs ni à payer de pénalités.
              </p>

              <p><strong>8.2 Exercice du droit</strong></p>
              <p>
                Pour exercer ce droit, l'utilisateur doit adresser sa demande par email 
                à <a href="mailto:contact@copro-protect.fr" className="text-blue-600 hover:underline">contact@copro-protect.fr</a> en 
                indiquant clairement sa volonté de se rétracter.
              </p>

              <p><strong>8.3 Remboursement</strong></p>
              <p>
                En cas de rétractation, DOM TECH & SERVICES remboursera l'intégralité des sommes versées 
                dans un délai de 14 jours à compter de la réception de la demande, via le même moyen de 
                paiement utilisé lors de la souscription.
              </p>

              <p><strong>8.4 Exception</strong></p>
              <p>
                Si l'utilisateur a expressément demandé à commencer à utiliser le service avant l'expiration 
                du délai de rétractation et a consommé des analyses SENTINELLE, le remboursement pourra être 
                réduit au prorata des analyses consommées.
              </p>
            </div>
          </section>

          {/* Article 9 : Durée et résiliation */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-gray-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Article 9 — Durée et résiliation</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p><strong>9.1 Durée</strong></p>
              <p>
                L'abonnement mensuel est souscrit sans engagement de durée et se renouvelle par tacite 
                reconduction chaque mois. L'abonnement annuel est souscrit pour une durée de 12 mois 
                et se renouvelle par tacite reconduction.
              </p>
              
              <p><strong>9.2 Résiliation par l'utilisateur</strong></p>
              <p>
                L'utilisateur peut résilier son abonnement à tout moment depuis son espace personnel 
                ou par email à <a href="mailto:contact@copro-protect.fr" className="text-blue-600 hover:underline">contact@copro-protect.fr</a>. 
                La résiliation prend effet :
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Abonnement mensuel</strong> : à la fin du mois en cours</li>
                <li><strong>Abonnement annuel</strong> : à la fin de la période annuelle en cours</li>
              </ul>
              <p>Aucun remboursement au prorata ne sera effectué pour la période restante.</p>
              
              <p><strong>9.3 Résiliation par l'éditeur</strong></p>
              <p>
                DOM TECH & SERVICES se réserve le droit de suspendre ou résilier un abonnement en cas de 
                manquement grave aux présentes CGU, notamment en cas d'usage interdit (article 5), après 
                mise en demeure restée sans effet pendant 15 jours.
              </p>
              
              <p><strong>9.4 Conséquences de la résiliation</strong></p>
              <p>
                À l'issue de la résiliation, l'utilisateur dispose d'un délai de <strong>30 jours</strong> pour 
                exporter ses données via la fonctionnalité "Exporter mes données" de la plateforme. 
                Passé ce délai, l'ensemble des données (documents, courriers, signalements, conversations, 
                contacts, échéances) sera définitivement supprimé.
              </p>

              <p><strong>9.5 Tarif Fondateur</strong></p>
              <p>
                Le bénéfice du tarif Fondateur est conditionné au maintien continu de l'abonnement. 
                Toute résiliation entraîne la perte définitive du tarif Fondateur. Une réinscription 
                ultérieure sera soumise au tarif Normal en vigueur.
              </p>
            </div>
          </section>

          {/* Article 10 : Droit applicable */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Scale className="w-5 h-5 text-slate-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Article 10 — Droit applicable et litiges</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>
                Les présentes CGU sont régies par le droit français.
              </p>
              <p>
                En cas de litige, les parties s'engagent à rechercher une solution amiable avant 
                toute action judiciaire. À défaut d'accord, les tribunaux compétents seront ceux 
                du ressort du siège social de DOM TECH & SERVICES (Boulogne-sur-Mer).
              </p>
              <p>
                Conformément aux dispositions du Code de la consommation, l'utilisateur consommateur est 
                informé qu'il peut recourir gratuitement à un médiateur de la consommation en vue de la 
                résolution amiable de tout litige. Le médiateur compétent sera communiqué sur simple 
                demande à <a href="mailto:contact@copro-protect.fr" className="text-blue-600 hover:underline">contact@copro-protect.fr</a>.
              </p>
            </div>
          </section>

          {/* Article 11 : Modifications */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <Edit className="w-5 h-5 text-violet-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Article 11 — Modifications des CGU</h2>
            </div>
            
            <div className="space-y-3 text-gray-600">
              <p>
                DOM TECH & SERVICES se réserve le droit de modifier les présentes CGU à tout moment. 
                Les utilisateurs seront informés de toute modification substantielle par email avec 
                un préavis minimum de 15 jours avant entrée en vigueur.
              </p>
              <p>
                La poursuite de l'utilisation de la plateforme après notification vaut acceptation 
                des nouvelles conditions. En cas de désaccord, l'utilisateur pourra résilier son 
                abonnement dans les conditions prévues à l'article 9.
              </p>
            </div>
          </section>

        </div>

        {/* Liens vers autres pages légales */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link 
            to="/mentions-legales" 
            className="text-blue-600 hover:underline"
          >
            Mentions légales
          </Link>
          <span className="text-gray-300">|</span>
          <Link 
            to="/politique-confidentialite" 
            className="text-blue-600 hover:underline"
          >
            Politique de confidentialité
          </Link>
        </div>
      </main>

      {/* Footer simple */}
      <footer className="bg-gray-900 text-gray-400 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm">
          © 2026 DOM TECH & SERVICES — Tous droits réservés
        </div>
      </footer>
    </div>
  );
};

export default CGUPage;
